import React, { useState } from "react";
import { FiDownload, FiTrash2, FiLoader, FiMusic, FiInfo } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { UploadCard, useJobHistory, formatSeconds, MAX_SONG_BYTES } from "./audioShared";

// AI Music Generation: upload a song, get the same song back as music only - the instruments, with no human
// voice. The work runs on the server in the background; this page polls for the result.
export default function MusicGenerationTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("instrumental");
  const [song, setSong] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [songError, setSongError] = useState("");

  const canSubmit = song && !submitting && !running;

  const submit = async () => {
    setError("");
    setSongError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "instrumental");
    form.append("original", song);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.original) return setSongError(data.original);
      return setError(errorMessage(data));
    }
    add(data);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Music Generation</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Upload a song and get the same song back as music only: the instruments and the beat, with the singing
        removed and no human voice.
      </p>

      <UploadCard
        step={1} kind="song" icon={FiMusic} title="Your song"
        help="A full song with vocals, up to 6 minutes."
        picked={song} onPick={setSong} onClear={() => setSong(null)} problem={songError}
        maxBytes={MAX_SONG_BYTES} convertOthers={false}
      />
      <p className="text-xs text-gray-500 mt-3 mb-6">
        Use music you own or have the right to edit. An AI model separates the voice from the music, so in a very
        busy mix a faint trace of the singing can occasionally remain.
      </p>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mb-6">{error}</p>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Uploading...</> : running ? <><FiLoader className="animate-spin" /> Working...</> : <><FiMusic /> Create music only (no vocals)</>}
      </button>
      {!canSubmit && !running && !submitting && <p className="text-xs text-gray-500 text-center mt-2">Add your song first</p>}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex items-center gap-3">
            <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
            <div>
              <p className="font-semibold">Removing the vocals... {formatSeconds(elapsed)}</p>
              <p className="text-sm text-gray-400">This usually takes under a minute for a short song, longer for a full song or when the free AI service is busy. You can stay on this page.</p>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 mt-4 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-brand-gradient animate-pulse" />
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The song could not be processed. Please try again."}</span>
        </div>
      )}

      {latest && latest.status === "completed" && latest.result_audio && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Your music (no vocals)</p>
          <audio controls src={latest.result_audio} className="w-full mb-4" />
          <a href={latest.result_audio} download className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-white text-sm font-medium transition-colors">
            <FiDownload /> Download music
          </a>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your music</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your results will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {history.map((job) => (
              <div key={job.id} className="rounded-xl bg-white/5 border border-navy-border p-4">
                <p className="text-sm text-gray-200 truncate">{job.original_name || "Song"}</p>
                <p className="text-xs text-gray-500 mb-2">
                  {new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? "Ready" : job.status === "failed" ? "Failed" : "Working..."}
                </p>
                {job.status === "completed" && job.result_audio && (
                  <div className="flex items-center gap-3">
                    <audio controls src={job.result_audio} className="w-full h-8" />
                    <a href={job.result_audio} download title="Download" className="text-gray-400 hover:text-accent-light shrink-0"><FiDownload size={16} /></a>
                    <button onClick={() => remove(job)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  </div>
                )}
                {job.status === "failed" && (
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs text-red-300">{job.error_message}</p>
                    <button onClick={() => remove(job)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
